/**
 * SmartCMR Analytics Tracker — Fase 1 + Fase 2
 *
 * Fase 1: Page views (existing, unchanged).
 * Fase 2: Click tracking, scroll depth, event batching.
 *
 * GDPR-compliant: NO tracking before explicit consent.
 * Privacy: NEVER reads DOM text, form fields, or user input.
 *          Only explicit data-track-id / data-track-label / data-track-category.
 * Performance: Non-blocking, batched, IntersectionObserver for scroll.
 *
 * TRACKING SCOPE: Only PUBLIC marketing routes (allowlist).
 * NEVER tracks on authenticated app pages.
 */

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const CONSENT_KEY = 'smartcmr_analytics_consent';
const CONSENT_PROOF_KEY = 'smartcmr_consent_proof';
const VISITOR_ID_KEY = 'smartcmr_visitor_id';
const SESSION_ID_KEY = 'smartcmr_session_id';
const UTM_KEY = 'smartcmr_utm_params';

// ── Public routes allowlist (Fase 2 tracking scope) ──
const PUBLIC_TRACKING_ROUTES = new Set([
  '/',
  '/contact',
  '/demo',
  '/login',
  '/register',
  '/forgot-password',
  '/blogs',
]);

function isTrackablePath(path) {
  return PUBLIC_TRACKING_ROUTES.has(path);
}

// ── Consent management ──

export function getConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
}

export function hasConsent() {
  return getConsent() === 'granted';
}

export function grantConsent() {
  try {
    localStorage.setItem(CONSENT_KEY, 'granted');
    const vid = getOrCreateVisitorId();
    getOrCreateSessionId();
    captureUtmParams();
    // Generate consent proof token: SHA-256(visitor_id + salt)
    _generateConsentProof(vid);
    // Start Fase 2 tracking after consent
    _startEngagementTracking();
  } catch {
    // Silently fail
  }
}

async function _generateConsentProof(visitorId) {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(visitorId + ':smartcmr_consent_granted');
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const proof = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    localStorage.setItem(CONSENT_PROOF_KEY, proof);
  } catch {
    // Fallback: simple proof for older browsers
    localStorage.setItem(CONSENT_PROOF_KEY, 'consent_v1_' + visitorId);
  }
}

function _getConsentProof() {
  try {
    return localStorage.getItem(CONSENT_PROOF_KEY) || '';
  } catch {
    return '';
  }
}

export function denyConsent() {
  try {
    localStorage.setItem(CONSENT_KEY, 'denied');
  } catch {
    // Silently fail
  }
}

export function withdrawConsent() {
  try {
    localStorage.setItem(CONSENT_KEY, 'denied');
    localStorage.removeItem(VISITOR_ID_KEY);
    localStorage.removeItem(CONSENT_PROOF_KEY);
    localStorage.removeItem(UTM_KEY);
    sessionStorage.removeItem(SESSION_ID_KEY);

    // Fase 2: Full cleanup on consent revocation
    _stopEngagementTracking();
  } catch {
    // Silently fail
  }
}

export function isConsentPending() {
  return getConsent() === null;
}

// ── ID management ──

function generateId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getOrCreateVisitorId() {
  if (!hasConsent()) return '';
  try {
    let id = localStorage.getItem(VISITOR_ID_KEY);
    if (!id) {
      id = generateId();
      localStorage.setItem(VISITOR_ID_KEY, id);
    }
    return id;
  } catch {
    return '';
  }
}

function getOrCreateSessionId() {
  if (!hasConsent()) return '';
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = generateId();
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return '';
  }
}

// ── UTM parameter capture ──

function captureUtmParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_term: params.get('utm_term') || '',
      utm_content: params.get('utm_content') || '',
    };

    const hasUtm = Object.values(utm).some((v) => v);
    if (hasUtm) {
      localStorage.setItem(UTM_KEY, JSON.stringify(utm));
    }
    return utm;
  } catch {
    return {};
  }
}

function getStoredUtmParams() {
  try {
    const stored = localStorage.getItem(UTM_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return captureUtmParams();
}

// ── Device detection ──

function getDeviceType() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad|playbook|silk/.test(ua)) return 'tablet';
  if (/mobile|android|iphone|ipod|opera mini|iemobile/.test(ua)) return 'mobile';
  return 'desktop';
}

// ══════════════════════════════════════════════════
// FASE 1: Page view tracking (unchanged contract)
// ══════════════════════════════════════════════════

function sendEvent(eventData) {
  if (!hasConsent()) return;

  const utm = getStoredUtmParams();
  const payload = {
    event_type: eventData.event_type || 'page_view',
    session_id: getOrCreateSessionId(),
    visitor_id: getOrCreateVisitorId(),
    consent_proof: _getConsentProof(),
    page_path: eventData.page_path || window.location.pathname,
    utm_source: utm.utm_source || '',
    utm_medium: utm.utm_medium || '',
    utm_campaign: utm.utm_campaign || '',
    utm_term: utm.utm_term || '',
    utm_content: utm.utm_content || '',
    referrer: document.referrer || '',
    device_type: getDeviceType(),
    user_agent: navigator.userAgent || '',
  };

  try {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API_URL}/analytics/event`, blob);
    } else {
      fetch(`${API_URL}/analytics/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Silently fail — analytics must never affect the user
  }
}

export function trackPageView(path) {
  if (!hasConsent()) return;
  sendEvent({ event_type: 'page_view', page_path: path || window.location.pathname });
}

// ══════════════════════════════════════════════════
// FASE 2: Click tracking, Scroll depth, Batching
// ══════════════════════════════════════════════════

// ── Event batching ──
const _eventQueue = [];
const FLUSH_INTERVAL = 3000;
const MAX_QUEUE_SIZE = 10;
let _flushTimer = null;

function _queueEvent(eventData) {
  if (!hasConsent()) return;

  const utm = getStoredUtmParams();
  const payload = {
    ...eventData,
    session_id: getOrCreateSessionId(),
    visitor_id: getOrCreateVisitorId(),
    consent_proof: _getConsentProof(),
    utm_source: utm.utm_source || '',
    utm_medium: utm.utm_medium || '',
    utm_campaign: utm.utm_campaign || '',
    utm_term: utm.utm_term || '',
    utm_content: utm.utm_content || '',
    referrer: document.referrer || '',
    device_type: getDeviceType(),
    user_agent: navigator.userAgent || '',
  };

  _eventQueue.push(payload);

  if (_eventQueue.length >= MAX_QUEUE_SIZE) {
    _flushEvents();
  }
}

function _flushEvents() {
  if (!_eventQueue.length) return;

  // Double-check consent before sending
  if (!hasConsent()) {
    _eventQueue.length = 0;
    return;
  }

  const batch = _eventQueue.splice(0);

  try {
    const blob = new Blob([JSON.stringify(batch)], { type: 'application/json' });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API_URL}/analytics/batch`, blob);
    } else {
      fetch(`${API_URL}/analytics/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(batch),
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Silent fail
  }
}

function _startFlushTimer() {
  if (_flushTimer) return;
  _flushTimer = setInterval(_flushEvents, FLUSH_INTERVAL);
}

function _stopFlushTimer() {
  if (_flushTimer) {
    clearInterval(_flushTimer);
    _flushTimer = null;
  }
}

// ── Click tracking (delegated, data-track-id only) ──

let _clickListenerAttached = false;

function _handleTrackedClick(e) {
  if (!hasConsent()) return;

  // Find closest element with data-track-id
  const tracked = e.target.closest('[data-track-id]');
  if (!tracked) return;

  // NEVER track on non-public routes
  if (!isTrackablePath(window.location.pathname)) return;

  // NEVER track form elements
  if (e.target.closest('input, textarea, select')) return;

  // Use ONLY explicit attributes — NO innerText/DOM text fallback
  const elementId = tracked.dataset.trackId || '';
  const elementLabel = tracked.dataset.trackLabel || ''; // Empty if not set
  const elementCategory = tracked.dataset.trackCategory || 'cta';

  _queueEvent({
    event_type: 'click',
    page_path: window.location.pathname,
    element_id: elementId,
    element_text: elementLabel,
    element_category: elementCategory,
  });
}

function _initClickTracking() {
  if (_clickListenerAttached) return;
  if (typeof document === 'undefined') return;
  document.addEventListener('click', _handleTrackedClick, { capture: true, passive: true });
  _clickListenerAttached = true;
}

function _removeClickTracking() {
  if (!_clickListenerAttached) return;
  document.removeEventListener('click', _handleTrackedClick, { capture: true });
  _clickListenerAttached = false;
}

// ── Scroll depth tracking (IntersectionObserver) ──

const SCROLL_MILESTONES = [25, 50, 75, 100];
const _scrolledMilestones = new Set(); // "sessionId:pagePath:milestone"
let _scrollObserver = null;
let _scrollResizeObserver = null;
let _scrollContainer = null;
let _scrollSentinels = [];

export function initScrollTracking(pagePath) {
  if (!hasConsent()) return;
  if (!isTrackablePath(pagePath)) return;

  cleanupScrollTracking();

  // Wait for content to render before measuring height
  requestAnimationFrame(() => {
    setTimeout(() => {
      _setupScrollSentinels(pagePath);
    }, 600);
  });
}

export function cleanupScrollTracking() {
  if (_scrollObserver) {
    _scrollObserver.disconnect();
    _scrollObserver = null;
  }
  if (_scrollResizeObserver) {
    _scrollResizeObserver.disconnect();
    _scrollResizeObserver = null;
  }
  _scrollSentinels.forEach((el) => {
    try { el.remove(); } catch { /* noop */ }
  });
  _scrollSentinels = [];
  if (_scrollContainer) {
    try { _scrollContainer.remove(); } catch { /* noop */ }
    _scrollContainer = null;
  }
}

function _setupScrollSentinels(pagePath) {
  if (typeof document === 'undefined') return;
  if (!hasConsent()) return;

  const docHeight = Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight
  );

  // Page doesn't scroll — no milestones to track
  if (docHeight <= window.innerHeight + 50) return;

  const sessionId = getOrCreateSessionId();

  // Container for sentinel elements (invisible, no layout impact)
  _scrollContainer = document.createElement('div');
  _scrollContainer.style.cssText =
    'position:absolute;top:0;left:0;width:1px;pointer-events:none;z-index:-9999;overflow:hidden;';
  _scrollContainer.setAttribute('data-analytics-scroll', 'true');
  document.body.appendChild(_scrollContainer);

  _scrollObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (!hasConsent()) return;

        const milestone = parseInt(entry.target.dataset.milestone, 10);
        const key = `${sessionId}:${pagePath}:${milestone}`;

        // Deduplicate: max 1x per session + page + milestone
        if (_scrolledMilestones.has(key)) continue;
        _scrolledMilestones.add(key);

        _queueEvent({
          event_type: 'scroll_depth',
          page_path: pagePath,
          scroll_milestone: milestone,
        });
      }
    },
    { threshold: 0 }
  );

  // Create and observe sentinel elements at each milestone
  _positionSentinels(docHeight, pagePath);

  // ResizeObserver: reposition sentinels when content height changes
  _scrollResizeObserver = new ResizeObserver(() => {
    const newHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight
    );
    _scrollSentinels.forEach((sentinel) => {
      const milestone = parseInt(sentinel.dataset.milestone, 10);
      sentinel.style.top = `${(milestone / 100) * newHeight}px`;
    });
  });
  _scrollResizeObserver.observe(document.body);
}

function _positionSentinels(docHeight) {
  SCROLL_MILESTONES.forEach((milestone) => {
    const sentinel = document.createElement('div');
    sentinel.style.cssText = `position:absolute;top:${
      (milestone / 100) * docHeight
    }px;width:1px;height:1px;pointer-events:none;`;
    sentinel.dataset.milestone = String(milestone);
    _scrollContainer.appendChild(sentinel);
    _scrollSentinels.push(sentinel);
    _scrollObserver.observe(sentinel);
  });
}

// ── Engagement tracking lifecycle ──

function _startEngagementTracking() {
  _initClickTracking();
  _startFlushTimer();
}

function _stopEngagementTracking() {
  // Stop click tracking
  _removeClickTracking();
  // Stop scroll tracking
  cleanupScrollTracking();
  // Clear queued events (NEVER send after revocation)
  _eventQueue.length = 0;
  _scrolledMilestones.clear();
  // Stop flush timer
  _stopFlushTimer();
}

// ── Auto-initialize if consent already given ──
if (typeof document !== 'undefined') {
  // Flush on page hide (unload)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      _flushEvents();
    }
  });

  // Start engagement tracking if consent was previously granted
  if (hasConsent()) {
    _startEngagementTracking();
  }
}

// ── Public API ──

export function getAcquisitionData() {
  if (!hasConsent()) return null;
  const utm = getStoredUtmParams();
  return {
    visitor_id: getOrCreateVisitorId(),
    session_id: getOrCreateSessionId(),
    utm_source: utm.utm_source || '',
    utm_medium: utm.utm_medium || '',
    utm_campaign: utm.utm_campaign || '',
    referrer: document.referrer || '',
    traffic_source: _classifySource(document.referrer, utm.utm_source),
  };
}

function _classifySource(referrer, utmSource) {
  if (utmSource) return utmSource;
  if (!referrer || typeof referrer !== 'string') return 'direct';
  const ref = referrer.toLowerCase();
  if (['google', 'bing', 'yahoo', 'duckduckgo'].some((s) => ref.includes(s))) return 'organic_search';
  if (['facebook', 'instagram', 'twitter', 'linkedin', 'tiktok'].some((s) => ref.includes(s))) return 'social';
  return 'referral';
}

export default {
  trackPageView,
  getAcquisitionData,
  hasConsent,
  grantConsent,
  denyConsent,
  withdrawConsent,
  isConsentPending,
  getConsent,
  initScrollTracking,
  cleanupScrollTracking,
  isTrackablePath,
};
